import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertCircle, CheckCircle2, Loader2, UserRound } from 'lucide-react';
import { toast } from 'sonner';
import { DashboardLayout } from '../components/DashboardLayout';
import { ProfileQuestionnaireFields } from '../components/ProfileQuestionnaireFields';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import { apiFetch } from '../lib/supabase';
import { emptyProfileForm, formatApiError, profilePayload, validateProfileForm } from '../lib/onboarding';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AccountProfilePage() {
  const { user, profileStatus, profileStatusUserId, refreshProfileStatus } = useAuth();
  const [form, setForm] = useState(emptyProfileForm);
  const [errors, setErrors] = useState({});
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const activeUserIdRef = useRef(user?.id ?? null);

  useEffect(() => {
    activeUserIdRef.current = user?.id ?? null;
    setForm(emptyProfileForm());
    setErrors({});
    setError('');
    setSaving(false);
  }, [user?.id]);

  useEffect(() => {
    if (!user?.id || profileStatusUserId !== user.id) return;
    const profile = profileStatus?.profile;
    if (!profile) return;
    setForm({
      ...emptyProfileForm(),
      firstName: profile.first_name || '',
      lastName: profile.last_name || '',
      phone: profile.phone_e164 || '',
      companyName: profile.company_name || '',
      accountType: profile.account_type || '',
    });
  }, [user?.id, profileStatusUserId, profileStatus?.profile]);

  const currentProfileStatus = profileStatusUserId === user?.id ? profileStatus : null;

  const setField = (name, value) => {
    setForm(current => ({ ...current, [name]: value }));
    setErrors(current => ({ ...current, [name]: undefined }));
  };

  const submit = async event => {
    event.preventDefault();
    const submitUserId = user?.id;
    if (!submitUserId) return;
    const nextErrors = validateProfileForm(form);
    setErrors(nextErrors);
    setError('');
    if (Object.keys(nextErrors).length) return;
    setSaving(true);
    try {
      const response = await apiFetch(`${API_URL}/api/profile/complete`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(profilePayload(form)),
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(formatApiError(body.detail));
      if (activeUserIdRef.current !== submitUserId) return;
      await refreshProfileStatus();
      if (activeUserIdRef.current !== submitUserId) return;
      setForm(current => ({ ...current, legalAccepted: false }));
      toast.success('Profile and legal acceptance saved.');
    } catch (err) {
      if (activeUserIdRef.current === submitUserId) {
        setError(err.message || 'Profile completion could not be saved.');
      }
    } finally {
      if (activeUserIdRef.current === submitUserId) setSaving(false);
    }
  };

  return (
    <DashboardLayout>
      <section className="mx-auto max-w-3xl" data-testid="account-profile-page">
        <div className="mb-8 flex items-start gap-4">
          <div className="grid h-11 w-11 shrink-0 place-items-center rounded-md bg-primary text-primary-foreground">
            <UserRound className="h-5 w-5" />
          </div>
          <div>
            <h1 className="text-4xl font-semibold sm:text-5xl" data-testid="account-profile-title">Account / Profile</h1>
            <p className="mt-3 max-w-2xl text-sm text-muted-foreground sm:text-base">
              Keep your AHA contact profile current and record your acceptance of the current Terms and Privacy Notice.
            </p>
          </div>
        </div>

        {currentProfileStatus?.complete && (
          <Alert className="mb-5 border-[hsl(var(--success))]/40" data-testid="account-profile-complete-alert">
            <CheckCircle2 className="h-4 w-4 text-[hsl(var(--success))]" />
            <AlertDescription>
              Current profile and legal acceptance recorded
              {currentProfileStatus.terms_accepted_at ? ` on ${new Date(currentProfileStatus.terms_accepted_at).toLocaleDateString()}` : ''}.
            </AlertDescription>
          </Alert>
        )}
        {currentProfileStatus?.available === false && (
          <Alert className="mb-5" data-testid="account-profile-unavailable-alert">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Profile storage is not available in this environment. Your existing AHA access is unaffected.
            </AlertDescription>
          </Alert>
        )}

        <form onSubmit={submit} className="card-elevated space-y-6 bg-card p-5 sm:p-8" data-testid="account-profile-form">
          {error && (
            <Alert variant="destructive" data-testid="account-profile-form-error">
              <AlertCircle className="h-4 w-4" /><AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          <ProfileQuestionnaireFields
            form={form}
            errors={errors}
            setField={setField}
            disabled={saving}
            testIdPrefix="account-profile"
          />
          <div className="flex flex-col-reverse gap-3 border-t pt-5 sm:flex-row sm:justify-between">
            <Button asChild type="button" variant="ghost">
              <Link to="/" data-testid="account-profile-later-link">Back to dashboard</Link>
            </Button>
            <Button type="submit" disabled={saving || currentProfileStatus?.available === false} data-testid="account-profile-submit-button">
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Save profile and acceptance
            </Button>
          </div>
        </form>
      </section>
    </DashboardLayout>
  );
}